import PropTypes from 'prop-types';
import React from 'react';
import {defineMessages, useIntl} from 'react-intl';

import Modal from '../../containers/modal.jsx';

import styles from '../domi-car-burn-center/domi-car-burn-center.css';

const messages = defineMessages({
    title: {
        id: 'gui.microbit.burnCenter.title',
        defaultMessage: 'micro:bit Burn Center',
        description: 'Title of the micro:bit burn center'
    },
    intro: {
        id: 'gui.microbit.burnCenter.intro',
        defaultMessage: 'Review the offline program before writing it to the micro:bit.',
        description: 'Introduction in the micro:bit burn center'
    },
    supported: {
        id: 'gui.microbit.burnCenter.supported',
        defaultMessage: 'USB burning available',
        description: 'WebUSB supported status in the micro:bit burn center'
    },
    unsupported: {
        id: 'gui.microbit.burnCenter.unsupported',
        defaultMessage: 'USB burning unavailable',
        description: 'WebUSB unsupported status in the micro:bit burn center'
    },
    browserHelp: {
        id: 'gui.microbit.burnCenter.browserHelp',
        defaultMessage: 'Use desktop Chrome or Edge and open Domi Scratch over HTTPS or localhost.',
        description: 'Help shown when WebUSB is unavailable'
    },
    previewTitle: {
        id: 'gui.microbit.burnCenter.previewTitle',
        defaultMessage: 'Program to be burned',
        description: 'Heading above the micro:bit instruction preview'
    },
    instructionCount: {
        id: 'gui.microbit.burnCenter.instructionCount',
        defaultMessage: '{count} instructions',
        description: 'Compiled instruction count in the micro:bit burn center'
    },
    preparing: {
        id: 'gui.microbit.burnCenter.preparing',
        defaultMessage: 'Compiling the Scratch program…',
        description: 'Status while compiling a micro:bit program'
    },
    ready: {
        id: 'gui.microbit.burnCenter.ready',
        defaultMessage: 'Compilation complete. Ready to burn.',
        description: 'Status after a micro:bit program compiles'
    },
    uploading: {
        id: 'gui.microbit.burnCenter.uploading',
        defaultMessage: 'Writing the program to the micro:bit…',
        description: 'Status while writing a program to a micro:bit'
    },
    success: {
        id: 'gui.microbit.burnCenter.success',
        defaultMessage: 'Burn successful. The program is now running on the micro:bit.',
        description: 'Status after successfully writing a micro:bit program'
    },
    error: {
        id: 'gui.microbit.burnCenter.error',
        defaultMessage: 'The program could not be burned.',
        description: 'Fallback error in the micro:bit burn center'
    },
    safety: {
        id: 'gui.microbit.burnCenter.safety',
        defaultMessage: 'Connect the micro:bit with a USB data cable. Do not unplug it while burning.',
        description: 'USB safety warning in the micro:bit burn center'
    },
    close: {
        id: 'gui.microbit.burnCenter.close',
        defaultMessage: 'Close Burn Center',
        description: 'Button that closes the micro:bit burn center'
    },
    compile: {
        id: 'gui.microbit.burnCenter.compile',
        defaultMessage: 'Recompile',
        description: 'Button that recompiles the current micro:bit program'
    },
    start: {
        id: 'gui.microbit.burnCenter.start',
        defaultMessage: 'Start burning',
        description: 'Button that starts writing a program to a micro:bit'
    },
    completed: {
        id: 'gui.microbit.burnCenter.completed',
        defaultMessage: 'Burn complete',
        description: 'Disabled action button after micro:bit burning completes'
    },
    displayLed: {
        id: 'gui.microbit.burnCenter.displayLed',
        defaultMessage: 'Display LED pattern',
        description: 'Compiled micro:bit LED instruction'
    },
    displayText: {
        id: 'gui.microbit.burnCenter.displayText',
        defaultMessage: 'Display text “{text}”',
        description: 'Compiled micro:bit text instruction'
    },
    wait: {
        id: 'gui.microbit.burnCenter.wait',
        defaultMessage: 'Wait {seconds} seconds',
        description: 'Compiled micro:bit wait instruction'
    },
    clear: {
        id: 'gui.microbit.burnCenter.clear',
        defaultMessage: 'Clear display',
        description: 'Compiled micro:bit clear-display instruction'
    },
    repeat: {
        id: 'gui.microbit.burnCenter.repeat',
        defaultMessage: 'Repeat {count} times',
        description: 'Compiled micro:bit repeat instruction'
    },
    repeatEnd: {
        id: 'gui.microbit.burnCenter.repeatEnd',
        defaultMessage: 'End repeat',
        description: 'Compiled micro:bit repeat-end instruction'
    },
    eventStart: {
        id: 'gui.microbit.burnCenter.eventStart',
        defaultMessage: 'When micro:bit starts',
        description: 'Compiled micro:bit start event'
    },
    eventButton: {
        id: 'gui.microbit.burnCenter.eventButton',
        defaultMessage: 'When button {button} is pressed',
        description: 'Compiled micro:bit button event'
    },
    eventButtonAny: {
        id: 'gui.microbit.burnCenter.eventButtonAny',
        defaultMessage: 'When any button is pressed',
        description: 'Compiled micro:bit any-button event'
    },
    eventTiltFront: {
        id: 'gui.microbit.burnCenter.eventTiltFront',
        defaultMessage: 'When tilted forward',
        description: 'Compiled micro:bit forward-tilt event'
    },
    eventTiltBack: {
        id: 'gui.microbit.burnCenter.eventTiltBack',
        defaultMessage: 'When tilted backward',
        description: 'Compiled micro:bit backward-tilt event'
    },
    eventTiltLeft: {
        id: 'gui.microbit.burnCenter.eventTiltLeft',
        defaultMessage: 'When tilted left',
        description: 'Compiled micro:bit left-tilt event'
    },
    eventTiltRight: {
        id: 'gui.microbit.burnCenter.eventTiltRight',
        defaultMessage: 'When tilted right',
        description: 'Compiled micro:bit right-tilt event'
    },
    eventTiltAny: {
        id: 'gui.microbit.burnCenter.eventTiltAny',
        defaultMessage: 'When tilted in any direction',
        description: 'Compiled micro:bit any-direction tilt event'
    },
    carMove: {
        id: 'gui.microbit.burnCenter.carMove',
        defaultMessage: 'Domi Car move {direction}',
        description: 'Compiled Domi Car movement instruction in a micro:bit program'
    },
    carStop: {
        id: 'gui.microbit.burnCenter.carStop',
        defaultMessage: 'Stop Domi Car',
        description: 'Compiled Domi Car stop instruction in a micro:bit program'
    },
    carLight: {
        id: 'gui.microbit.burnCenter.carLight',
        defaultMessage: 'Turn Domi Car light {state}',
        description: 'Compiled Domi Car light instruction in a micro:bit program'
    }
});

const getInstructionLabel = (instruction, intl) => {
    if (instruction === 'EVENT START') return intl.formatMessage(messages.eventStart);
    if (instruction === 'EVENT BUTTON ANY') return intl.formatMessage(messages.eventButtonAny);
    if (instruction.startsWith('EVENT BUTTON ')) {
        return intl.formatMessage(messages.eventButton, {button: instruction.slice(13)});
    }
    if (instruction.startsWith('EVENT TILT ')) {
        const direction = instruction.slice(11).toLowerCase();
        const messageKey = `eventTilt${direction.charAt(0).toUpperCase()}${direction.slice(1)}`;
        if (messages[messageKey]) return intl.formatMessage(messages[messageKey]);
    }
    if (instruction.startsWith('CAR MOVE ')) {
        return intl.formatMessage(messages.carMove, {direction: instruction.slice(9)});
    }
    if (instruction === 'CAR STOP') return intl.formatMessage(messages.carStop);
    if (instruction.startsWith('CAR LIGHT ')) {
        return intl.formatMessage(messages.carLight, {state: instruction.endsWith('1') ? 'on' : 'off'});
    }
    if (instruction.startsWith('LED ')) return intl.formatMessage(messages.displayLed);
    if (instruction.startsWith('TEXT ')) {
        return intl.formatMessage(messages.displayText, {text: instruction.slice(5)});
    }
    if (instruction.startsWith('WAIT ')) {
        const seconds = Number((Number(instruction.slice(5)) / 1000).toFixed(2));
        return intl.formatMessage(messages.wait, {seconds});
    }
    if (instruction.startsWith('REPEAT ')) {
        return intl.formatMessage(messages.repeat, {count: instruction.slice(7)});
    }
    if (instruction === 'CLEAR') return intl.formatMessage(messages.clear);
    if (instruction === 'END') return intl.formatMessage(messages.repeatEnd);
    return instruction;
};

const phaseMessages = {
    preparing: messages.preparing,
    ready: messages.ready,
    invalid: messages.error,
    uploading: messages.uploading,
    success: messages.success,
    error: messages.error
};

const MicroBitBurnCenter = ({
    instructionCount,
    instructions,
    isOpen,
    message,
    onClose,
    onCompile,
    onStart,
    phase,
    progress,
    webUsbSupported
}) => {
    const intl = useIntl();
    if (!isOpen) return null;

    const canStart = webUsbSupported && phase === 'ready' && instructions.length > 0;
    const showProgress = ['uploading', 'success', 'error'].includes(phase);
    const statusMessage = message || intl.formatMessage(phaseMessages[phase] || messages.preparing);
    const startLabel = phase === 'success' ? messages.completed : messages.start;

    return (
        <Modal
            className={styles.modal}
            contentLabel={intl.formatMessage(messages.title)}
            id="microbit-burn-center"
            onRequestClose={onClose}
        >
            <div className={styles.body}>
                <div className={styles.intro}>
                    <p>{intl.formatMessage(messages.intro)}</p>
                    <span className={webUsbSupported ? styles.connectedBadge : styles.disconnectedBadge}>
                        {intl.formatMessage(webUsbSupported ? messages.supported : messages.unsupported)}
                    </span>
                </div>

                {webUsbSupported ? null : (
                    <div className={styles.warningBox}>{intl.formatMessage(messages.browserHelp)}</div>
                )}
                {message && ['invalid', 'error'].includes(phase) ? (
                    <div className={styles.warningBox}>{statusMessage}</div>
                ) : null}

                <section className={styles.previewCard}>
                    <div className={styles.previewHeader}>
                        <h3>{intl.formatMessage(messages.previewTitle)}</h3>
                        <span>{intl.formatMessage(messages.instructionCount, {count: instructionCount})}</span>
                    </div>
                    {phase === 'preparing' ? (
                        <div className={styles.emptyPreview}>{intl.formatMessage(messages.preparing)}</div>
                    ) : instructions.length ? (
                        <ol className={styles.instructionList}>
                            {instructions.map((instruction, index) => (
                                <li key={`${index}-${instruction}`}>
                                    <span>{getInstructionLabel(instruction, intl)}</span>
                                    <code>{instruction}</code>
                                </li>
                            ))}
                        </ol>
                    ) : (
                        <div className={styles.emptyPreview}>{statusMessage}</div>
                    )}
                </section>

                <div className={styles.safetyBox}>{intl.formatMessage(messages.safety)}</div>

                {showProgress ? (
                    <section
                        aria-live="polite"
                        className={`${styles.progressCard} ${
                            phase === 'error' ? styles.progressError : ''
                        } ${phase === 'success' ? styles.progressSuccess : ''}`}
                    >
                        <div className={styles.progressLabels}>
                            <span>{statusMessage}</span>
                            <strong>{progress}%</strong>
                        </div>
                        <div
                            aria-label={statusMessage}
                            aria-valuemax="100"
                            aria-valuemin="0"
                            aria-valuenow={progress}
                            className={styles.progressTrack}
                            role="progressbar"
                        >
                            <div
                                className={styles.progressFill}
                                style={{width: `${progress}%`}}
                            />
                        </div>
                    </section>
                ) : null}

                <div className={styles.actions}>
                    <button
                        className={styles.secondaryButton}
                        disabled={phase === 'uploading'}
                        type="button"
                        onClick={onClose}
                    >
                        {intl.formatMessage(messages.close)}
                    </button>
                    <button
                        className={styles.secondaryButton}
                        disabled={['preparing', 'uploading'].includes(phase)}
                        type="button"
                        onClick={onCompile}
                    >
                        {intl.formatMessage(messages.compile)}
                    </button>
                    <button
                        className={styles.primaryButton}
                        disabled={!canStart}
                        type="button"
                        onClick={onStart}
                    >
                        {intl.formatMessage(startLabel)}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

MicroBitBurnCenter.propTypes = {
    instructionCount: PropTypes.number,
    instructions: PropTypes.arrayOf(PropTypes.string),
    isOpen: PropTypes.bool,
    message: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    onCompile: PropTypes.func.isRequired,
    onStart: PropTypes.func.isRequired,
    phase: PropTypes.oneOf(['preparing', 'ready', 'invalid', 'uploading', 'success', 'error']),
    progress: PropTypes.number,
    webUsbSupported: PropTypes.bool
};

MicroBitBurnCenter.defaultProps = {
    instructionCount: 0,
    instructions: [],
    isOpen: false,
    message: '',
    phase: 'preparing',
    progress: 0,
    webUsbSupported: false
};

export default MicroBitBurnCenter;
